<template>
  <teleport
    v-if="hasBeenActive"
    to="body"
    :disabled="!isAppView"
  >
    <div
      :class="{
        'snackbar-fi': true,
        'snackbar-fi--active': isActive,
        'snackbar-fi--app-view': isAppView,
      }"
      data-test="snackbar"
      :data-test-2="isActive"
      @mouseenter="lockTransition"
      @mouseleave="setTimeoutForSnackbar(1000)"
    >
      <component
        :is="isCart ? 'div' : 'a'"
        class="snackbar-fi__wrapper"
        :href="isCart ? undefined : $fischer.pages.cart().url"
        rel="nofollow"
        @click="goToCart"
      >
        <grid-container-fi>
          <div class="fi-col-start-1 fi-col-end-13 lg:fi-col-start-2 lg:fi-col-end-12">
            <div class="snackbar-fi__content-wrapper">
              <div class="snackbar-fi__content">
                <div class="snackbar-fi__counter">
                  <icon-fi
                    class="snackbar-fi__icon"
                    icon="shopping-cart"
                  />
                  <span class="snackbar-fi__counter-count">{{ countProducts }}</span>
                </div>

                <div
                  v-if="$dict.get('online-Shop.Product.Added-to-Cart')"
                  class="snackbar-fi__message"
                >
                  {{ $dict.get('online-Shop.Product.Added-to-Cart') }}
                </div>
              </div>

              <div
                v-if="!isCart"
                class="snackbar-fi__controls"
              >
                <div
                  class="snackbar-fi__link button-fi__text--tertiary button-fi__text--16 lg:button-fi__text--18"
                >
                  <span class="button-fi__text fi-text-16 lg:fi-text-18">
                    <span class="button-fi__text-span">
                      {{ $dict.get('online-Shop.Product.View-Cart') }}
                    </span>
                    <arrow-icon />
                  </span>
                </div>
              </div>
            </div>
          </div>
        </grid-container-fi>
      </component>
    </div>
  </teleport>
</template>

<script setup lang="ts">
import { ArrowIcon, IconFi } from 'atoms';
import { computed, nextTick, onMounted, ref, watch } from 'vue';

import GridContainerFi from '@/components/grid-fi/GridContainerFi.vue';
import ShopEventBus from '@/components/shop-modules/shop-event-bus';
import { useAppEvent } from '@/composables/appEvent';
import { useFischer } from '@/composables/fischerPlugin';
import { useNotificationStore } from '@/store/pinia/notification';

defineOptions({ name: 'SnackbarFi' });

const fischer = useFischer();
const notificationStore = useNotificationStore();

const countProducts = ref(0);
const isActive = ref(false);
const timeOut = ref<ReturnType<typeof setTimeout> | null>(null);
const hasBeenActive = ref(false);
const { sendAppEvent } = useAppEvent();

const isCart = computed(() => fischer.uri() === fischer.pages.cart().url);

function lockTransition() {
  if (!isActive.value) {
    return;
  }

  if (timeOut.value) {
    clearTimeout(timeOut.value);
  }
  timeOut.value = null;
}

function setTimeoutForSnackbar(delay = 5000) {
  timeOut.value = setTimeout(() => {
    isActive.value = false;
    timeOut.value = null;
  }, delay);
}

const goToCart = (e: MouseEvent) => {
  if (isCart.value || !fischer.app.isAppView()) {
    return;
  }
  e.preventDefault();
  sendAppEvent('go-to-cart');
};

watch(isActive, (value) => {
  notificationStore.setSnackbarVisible(value);
});

onMounted(() => {
  ShopEventBus.$on('ProductAddToCart', async (value: boolean) => {
    if (!hasBeenActive.value) {
      hasBeenActive.value = true;
      await Promise.all([nextTick(), new Promise((resolve) => setTimeout(resolve, 10))]);
    }
    if (value === true && !timeOut.value) {
      isActive.value = value;

      setTimeoutForSnackbar();
    } else {
      if (timeOut.value) {
        clearTimeout(timeOut.value);
      }
      isActive.value = false;
      timeOut.value = null;
    }
  });

  ShopEventBus.$on('ShoppingcartCounter', (value: number) => {
    countProducts.value = value;
  });
});
</script>

<style src="./snackbar-fi-unscoped" />

<style scoped src="./snackbar-fi" />
